import * as api from "../../Service/api";

export const normalizeCustomerMatches = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.content)) return data.content;
    return [];
};

const customerMatchesQuery = (customer, query) => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return false;

    const name = String(customer.name || "").trim().toLowerCase();
    const phone = String(customer.phoneNo || "").trim().toLowerCase();
    const reference = String(customer.reference || "").trim().toLowerCase();
    const customerNum = String(customer.customerNum || "").trim().toLowerCase();

    if (phone.includes(normalized) || reference.includes(normalized) || customerNum.includes(normalized)) {
        return true;
    }

    if (!name) return false;
    if (name.includes(normalized)) return true;

    // Match name tokens that start with the query (e.g. "dor" → "Doreen")
    return name.split(/\s+/).some((token) => token.startsWith(normalized));
};

export const filterCustomerMatches = (matches, query) => (
    matches.filter((customer) => customer?.id && customerMatchesQuery(customer, query))
);

const lookupSubscribers = async (businessId, query) => {
    // Prefer exact lookup (same as Universal Search), then simple for broader partial matches
    const exactResponse = await api.getExactFilteredBusinessCustomers(businessId, query);
    if (exactResponse.ok) {
        const exactMatches = filterCustomerMatches(normalizeCustomerMatches(exactResponse.data), query);
        if (exactMatches.length) {
            return exactMatches;
        }
    }

    const simpleResponse = await api.getFilteredBusinessCustomers(businessId, query);
    if (!simpleResponse.ok) {
        return [];
    }
    return filterCustomerMatches(normalizeCustomerMatches(simpleResponse.data), query);
};

export const parseBcIdsParam = (value) => {
    if (!value) return [];
    return String(value)
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);
};

export const toBcIdsParam = (bcIds = []) => (
    bcIds.filter(Boolean).map(String).join(",")
);

export const resolveSubscribersByQuery = async (businessId, query) => {
    const matches = await lookupSubscribers(businessId, query);
    const bcIds = matches.map((customer) => customer.id).filter(Boolean);
    return {
        ok: true,
        matches,
        bcIds,
        bcIdsParam: toBcIdsParam(bcIds),
    };
};

const buildFilter = (baseFilter, bcId) => {
    const params = new URLSearchParams(baseFilter.startsWith("?") ? baseFilter.slice(1) : baseFilter);
    params.delete("bcId");
    params.delete("bcIds");
    params.delete("searchText");
    params.delete("q");
    params.set("bcId", String(bcId));
    return `?${params.toString()}`;
};

const normalizePage = (data) => {
    if (!data) return { content: [], totalElements: 0 };
    if (Array.isArray(data)) return { content: data, totalElements: data.length };
    return {
        content: Array.isArray(data.content) ? data.content : [],
        totalElements: Number(data.totalElements ?? data.content?.length ?? 0),
    };
};

const mergePages = (pages, sortKey) => {
    const seen = new Set();
    const content = [];

    pages.forEach((page) => {
        (page.content || []).forEach((row) => {
            const key = row?.id ?? `${row?.paymentId || ""}-${row?.receivedAt || row?.scheduledFor || row?.externallyReceivedOn || ""}`;
            if (seen.has(key)) return;
            seen.add(key);
            content.push(row);
        });
    });

    content.sort((a, b) => {
        const aTime = new Date(a?.[sortKey] || 0).getTime();
        const bTime = new Date(b?.[sortKey] || 0).getTime();
        return bTime - aTime;
    });

    return {
        content,
        totalElements: content.length,
    };
};

const sumTotals = (totalsList = []) => {
    return totalsList.reduce((acc, totals) => {
        if (!totals || typeof totals !== "object") return acc;
        Object.keys(totals).forEach((key) => {
            const value = Number(totals[key]);
            if (Number.isNaN(value)) return;
            acc[key] = (Number(acc[key]) || 0) + value;
        });
        return acc;
    }, {});
};

export const fetchPaymentsForBcIds = async ({
    businessId,
    type,
    baseFilter,
    bcIds,
    page = 0,
    size = 15,
}) => {
    const ids = (bcIds || []).filter(Boolean);
    if (!ids.length) {
        return { pageData: { content: [], totalElements: 0 }, totals: {} };
    }

    const fetchSize = Math.max(size * 5, 100);

    const paymentRequests = ids.map((bcId) => {
        const filter = buildFilter(baseFilter, bcId);
        switch (type) {
            case "missed":
                return api.getMissedPaymentTrack(businessId, filter, { page: 0, size: fetchSize, sort: "scheduledFor,desc" });
            case "external":
                return api.getExternalPaymentTrack(businessId, filter, { page: 0, size: fetchSize, sort: "externallyReceivedOn,desc" });
            case "accounts":
                return api.getAccountsPaymentSearch(businessId, `${filter}${filter.includes("?") ? "&" : "?"}sort=scheduledFor,desc`, { page: 0, size: fetchSize });
            case "collected":
            default:
                return api.getCollectedPaymentTrack(businessId, filter, { page: 0, size: fetchSize, sort: "receivedAt,desc" });
        }
    });

    const totalsRequests = ids.map((bcId) => {
        const filter = buildFilter(baseFilter, bcId);
        switch (type) {
            case "accounts":
                return api.getAccountsPaymentTotals(businessId, filter, {});
            case "external":
                return api.getExternalPaymentTotals(businessId, filter, {});
            case "collected":
                return api.getFilteredDataTotals(businessId, filter, {});
            default:
                return Promise.resolve({ ok: true, data: {} });
        }
    });

    const [paymentResponses, totalsResponses] = await Promise.all([
        Promise.all(paymentRequests),
        Promise.all(totalsRequests),
    ]);

    const sortKey = type === "missed" || type === "accounts"
        ? "scheduledFor"
        : type === "external"
            ? "externallyReceivedOn"
            : "receivedAt";

    const merged = mergePages(
        paymentResponses.filter((response) => response.ok).map((response) => normalizePage(response.data)),
        sortKey
    );

    const start = page * size;
    const pagedContent = merged.content.slice(start, start + size);

    return {
        pageData: {
            content: pagedContent,
            totalElements: merged.totalElements,
        },
        totals: sumTotals(
            totalsResponses
                .filter((response) => response.ok)
                .map((response) => response.data)
        ),
    };
};
